import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import { attachmentMetadataToDisplay } from "@/mappers/email/displayAttachmentMapper";
import eventDetailMapper from "@/mappers/email/eventDetailMapper";
import linkObjectMapper from "@/mappers/email/linkObjectMapper";
import {
    useCustomViews,
    useSdkEvent,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import useFetchErrorNotifier from "../utils/useFetchErrorNotifier";

// Contenu (documents / liens / événements) d'une CustomView, source = SDK (backend) et NON dérivation front
// sur la liste paginée → on affiche l'INTÉGRALITÉ de la view (cf. AB#1727). La dédup (attachments/links) et le
// filtrage des events passés sont déjà faits côté SDK. Modèle : useGetCustomViewTodos (3 queries partageant
// l'invalidation + un seul abonnement event-bus, débounce aligné).
//
// ───────────────────────────────────────────────────────────────────────────────────────────────
// TODO (devs futurs AB#1727 — non implémentés, cf. Doc/AB#1727/recette-campagne.md §Journal) :
//  1. LOADER : sur une grosse view (ex. 1188 mails ⇒ ~24 lots /mails/content), le contenu met du temps à
//     arriver. Le hook expose déjà `isDocumentsLoading / isLinksLoading / isEventsLoading` → les consommer
//     pour afficher un loader dans les widgets pendant le chargement.
//  2. RENDU PROGRESSIF : afficher le contenu au fil de l'eau (par lot) au lieu d'attendre la fin des 24 lots.
//     L'infra SDK est prête (fetch batché + store par-mail alimenté lot par lot). Il faudra : côté SDK,
//     `fetchMissingMailViewContentIfAny` retourne au 1er lot + émet un event par lot (viewId-scopé, ex.
//     `custom-view-content-updated`) ; côté hook, s'y abonner (comme `custom-view-list-updated`) pour
//     re-lire au fil de l'eau. Prévoir un flag `PROGRESSIVE_VIEW_CONTENT` (désactivable) — cf. Étape 1-bis.
// ───────────────────────────────────────────────────────────────────────────────────────────────
type UseGetCustomViewContentOptions = {
    documents?: boolean;
    links?: boolean;
    events?: boolean;
};

const INVALIDATE_DEBOUNCE_MS = 2300;

const useGetCustomViewContent = (
    viewId?: string,
    {
        documents = false,
        links = false,
        events = false,
    }: UseGetCustomViewContentOptions = {},
) => {
    const customViews = useCustomViews();
    const notifyFetchError = useFetchErrorNotifier();
    const queryClient = useQueryClient();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const viewIdRef = useRef(viewId);
    viewIdRef.current = viewId;

    // Les queryFn laissent l'erreur remonter (état `isError`) ; le toast est affiché UNE seule fois via un effet
    // dédié plus bas (les 3 queries partagent le même fetch SDK → éviter jusqu'à 3 toasts identiques).
    const documentsQuery = useQuery({
        queryKey: queryKeys.customView.attachments(viewId!),
        queryFn: async () => {
            if (!viewId) {
                throw new Error("View ID is undefined");
            }

            const attachments =
                await customViews.getCustomViewAttachments(viewId);
            return attachments.map(attachmentMetadataToDisplay);
        },
        enabled: !!viewId && documents,
        staleTime: STALE_TIME.realtime,
    });

    const linksQuery = useQuery({
        queryKey: queryKeys.customView.links(viewId!),
        queryFn: async () => {
            if (!viewId) {
                throw new Error("View ID is undefined");
            }

            const viewLinks = await customViews.getCustomViewLinks(viewId);
            return viewLinks.map(linkObjectMapper);
        },
        enabled: !!viewId && links,
        staleTime: STALE_TIME.realtime,
    });

    const eventsQuery = useQuery({
        queryKey: queryKeys.customView.events(viewId!),
        queryFn: async () => {
            if (!viewId) {
                throw new Error("View ID is undefined");
            }

            const viewEvents = await customViews.getCustomViewEvents(viewId);

            // `sentAt` = sentDateTime du mail porteur (ajout AB#1727 HIGH-1 au contrat /mails/content) : sert à
            // ordonner les copies successives d'une réunion (invitation → update → annulation). Le Backend le
            // renseigne désormais pour CHAQUE event de /mails/content → l'absence « ne devrait pas arriver » par
            // ce chemin.
            // Fallback DÉTERMINISTE : `new Date(0)` (epoch) → un event sans `sentAt` est trié comme le plus
            // ancien, de façon STABLE et reproductible. On évite volontairement `new Date()` (horloge courante) :
            // non déterministe, deux rendus successifs classeraient le même event à des positions différentes.
            // Un log DEV signale la régression de contrat éventuelle sans bruit en prod.
            return viewEvents.map((event) => {
                if (!event.sentAt && import.meta.env.DEV) {
                    console.warn(
                        `[AB#1727] event sans sentAt dans getCustomViewEvents (viewId=${viewId}) — ` +
                            "fallback epoch (tri déterministe). Le Backend /mails/content devrait toujours le fournir.",
                    );
                }
                return eventDetailMapper(
                    event.sentAt ? new Date(event.sentAt) : new Date(0),
                    event,
                );
            });
        },
        enabled: !!viewId && events,
        staleTime: STALE_TIME.realtime,
    });

    // Toast UNIQUE : une seule notification quand au moins une des 3 sous-requêtes échoue (re-déclenchée
    // seulement après un retour à l'état sain), plutôt qu'un toast par query. Appel sans `queryKey` :
    // l'erreur est déjà enregistrée quand l'effet s'exécute.
    const hasError =
        documentsQuery.isError || linksQuery.isError || eventsQuery.isError;

    useEffect(() => {
        if (hasError) {
            notifyFetchError({ titleTranslationKey: "errors.getViewContent" });
        }
    }, [hasError]);

    // La CustomView évolue (mails ajoutés/retirés) → on ré-invalide les 3 sous-caches, débouncé pour absorber
    // les rafales d'événements SDK (même stratégie que useGetCustomViewTodos).
    const invalidateContent = () => {
        const currentViewId = viewIdRef.current;
        if (!currentViewId) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            queryClient.invalidateQueries({
                queryKey: queryKeys.customView.attachments(currentViewId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.customView.links(currentViewId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKeys.customView.events(currentViewId),
            });
            timeoutRef.current = null;
        }, INVALIDATE_DEBOUNCE_MS);
    };

    useSdkEvent("mail-updated", invalidateContent);

    useSdkEvent("custom-view-list-updated", invalidateContent);

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    return {
        documents: documentsQuery.data ?? [],
        links: linksQuery.data ?? [],
        events: eventsQuery.data ?? [],
        isDocumentsLoading: documentsQuery.isLoading,
        isLinksLoading: linksQuery.isLoading,
        isEventsLoading: eventsQuery.isLoading,
    };
};

export default useGetCustomViewContent;
